import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ImageWithBasePath from "../imageWithBasePath";
import { all_routes } from "../../../feature-module/router/all_routes";
import { useDispatch, useSelector } from "react-redux";
import {
  setExpandMenu,
  setMiniSidebar,
  setMobileSidebar,
} from "../../data/redux/commonSlice";
import { HorizontalSidebarData } from "../../data/json/horizontalSidebar";
import { AuthContext } from "../../../context/authcontext";
import { storeCurrencyCode } from "../../../components/currencyFormat";
import UniversalSearch from "../../../components/filters/UniversalSearch";
import TaskNotification from "../../../components/notification/taskNotification";
import { ChatService } from "../../../feature-module/application/components/chat/chatService";
import { AgentStatus, UserAgentStatus } from "../../../feature-module/application/components/chat/chatTypes";

const Header = () => {
  const [subOpen, setSubopen] = useState<any>("");
  const [subsidebar, setSubsidebar] = useState("");
  const { auth, selectedTenantId, setSelectedTenantId, logout } =
    useContext(AuthContext);
  const navigate = useNavigate();

  const [isFullscreen, setIsFullscreen] = useState(false);
  const DEFAULT_AVATAR =
    "https://cloudee-sales-crm-artifact.s3.ap-southeast-7.amazonaws.com/public-artifacts/user/male.png";

  const avatarUrl = auth?.avatar_url || DEFAULT_AVATAR;
  // Sample tenants data - replace with your actual tenants data
  // const { auth } = useContext(AuthContext);
  const tenants = auth?.tenants || [];
  const [tenantSearch, setTenantSearch] = useState<string>("");
  const [mobileTenantSearch, setMobileTenantSearch] = useState<string>("");
  const [mobileTenantDropdownOpen, setMobileTenantDropdownOpen] =
    useState<boolean>(false);

  // Agent Status states and hooks
  const [agentStatuses, setAgentStatuses] = useState<AgentStatus[]>([]);
  const [currentUserAgentStatus, setCurrentUserAgentStatus] = useState<UserAgentStatus | null>(null);
  const [isAgentStatusDropdownOpen, setIsAgentStatusDropdownOpen] = useState(false);
  const [isAgentStatusLoading, setIsAgentStatusLoading] = useState(true);
  const agentStatusDropdownRef = useRef<HTMLDivElement>(null);

  const userId = auth?.user_id;

  const internalBotIdRef = useRef<number | null>(null);
  const headerUserIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    headerUserIdRef.current = userId !== undefined && userId !== null ? Number(userId) : undefined;
  }, [userId]);

  useEffect(() => {
    if (!userId || !selectedTenantId) return;

    const fetchAndSync = async () => {
      try {
        const response = await ChatService.getExternalChatTenantBot(Number(selectedTenantId));
        const results: any[] = response?.results || [];
        const internalBot = results.find(
          (bot: any) => bot.is_authorized === true && bot.chat_type?.chat_type_key === "INTERNAL"
        );
        const internalBotId = internalBot?.external_chat_tenant_bot_id || null;
        internalBotIdRef.current = internalBotId;
        if (internalBotId) {
          await ChatService.syncReadStatus(Number(userId), internalBotId);
        }
      } catch (_) {}
    };

    fetchAndSync();
  }, [userId, selectedTenantId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) return;
      const lastActivityTime = localStorage.getItem("lastActivityTime");
      const authExpiryTime = localStorage.getItem("authExpiryTime");
      if (!lastActivityTime || !authExpiryTime) return;
      const now = Date.now();
      if (now >= Number(authExpiryTime)) return;
      const timeSinceLastActivity = now - Number(lastActivityTime);
      if (timeSinceLastActivity < 300000) return;
      if (!internalBotIdRef.current || !headerUserIdRef.current) return;
      ChatService.syncReadStatus(headerUserIdRef.current, internalBotIdRef.current).catch(() => {});
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (agentStatusDropdownRef.current && !agentStatusDropdownRef.current.contains(event.target as Node)) {
        setIsAgentStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchAgentStatusData = async () => {
      setIsAgentStatusLoading(true);
      try {
        const statusesResponse = await ChatService.getAgentStatuses();
        setAgentStatuses(statusesResponse.results.filter((status: AgentStatus) => status.agent_status_key !== 'OFFLINE'));

        if (userId) {
          const userStatusResponse = await ChatService.getCurrentUserAgentStatus(Number(userId));
          if (userStatusResponse.results.length > 0) {
            setCurrentUserAgentStatus(userStatusResponse.results[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching agent status", error);
      } finally {
        setIsAgentStatusLoading(false);
      }
    };
    if (userId) {
      fetchAgentStatusData();
    } else {
      setIsAgentStatusLoading(false);
    }
  }, [userId]);

  const handleAgentStatusChange = async (statusId: number) => {
    setIsAgentStatusDropdownOpen(false);
    try {
      if (currentUserAgentStatus) {
        const updated = await ChatService.updateUserAgentStatus(currentUserAgentStatus.user_agent_status_id, {
          agent_status_id: statusId
        });
        setCurrentUserAgentStatus(updated);
      } else {
        if (userId && selectedTenantId) {
          const created = await ChatService.createUserAgentStatus({
            user_id: Number(userId),
            tenant_id: Number(selectedTenantId),
            agent_status_id: statusId
          });
          setCurrentUserAgentStatus(created);
        }
      }
    } catch (error) {
      console.error("Error updating agent status", error);
    }
  };

  // Initialize selected tenant from localStorage on component mount
  useEffect(() => {
    const savedTenantId = localStorage.getItem("selectedTenantId");

    // Check if user is Super Admin
    const isSuperAdmin =
      tenants.some(
        (t: any) =>
          t.role_key?.toLowerCase() === "superadmin" ||
          t.role_key?.toLowerCase() === "super_admin" ||
          t.role_name?.toLowerCase() === "superadmin" ||
          t.role_name?.toLowerCase() === "super_admin" ||
          t.role_key?.toLowerCase() === "platform_analyst" ||
          t.role_name?.toLowerCase() === "platformanalyst" ||
          t.role_key?.toLowerCase() === "apiadmin" ||
          t.role_key?.toLowerCase() === "api_admin" ||
          t.role_name?.toLowerCase() === "apiadmin" ||
          t.role_name?.toLowerCase() === "api_admin"
      ) || false;

    // For Super Admin, don't auto-restore tenant selection from localStorage
    // Let them manually select a tenant
    if (isSuperAdmin) {
      return;
    }

    if (savedTenantId && savedTenantId !== selectedTenantId) {
      // Only update if the saved ID exists and is different from current selection
      setSelectedTenantId(savedTenantId);
    }
  }, [auth, setSelectedTenantId, selectedTenantId, tenants]);

  // Enhanced tenant filtering with partial matching
  // Shows tenants where the search term exists anywhere in the tenant name
  const filteredTenants = tenants.filter((t: any) => {
    const term = tenantSearch.trim().toLowerCase();
    if (!term) return true;

    const tenantName = t.tenant_name.toLowerCase();

    // Check if the search term exists anywhere in the tenant name
    return tenantName.includes(term);
  });

  // Mobile tenant filtering
  const filteredMobileTenants = tenants.filter((t: any) => {
    const term = mobileTenantSearch.trim().toLowerCase();
    if (!term) return true;

    const tenantName = t.tenant_name.toLowerCase();

    // Check if the search term exists anywhere in the tenant name
    return tenantName.includes(term);
  });

  // Check if user is Super Admin
  const isSuperAdmin =
    tenants.some(
      (t: any) =>
        t.role_key?.toLowerCase() === "superadmin" ||
        t.role_key?.toLowerCase() === "super_admin" ||
        t.role_name?.toLowerCase() === "superadmin" ||
        t.role_name?.toLowerCase() === "super_admin" ||
        t.role_key?.toLowerCase() === "platform_analyst" ||
        t.role_name?.toLowerCase() === "platformanalyst" ||
        t.role_key?.toLowerCase() === "apiadmin" ||
        t.role_key?.toLowerCase() === "api_admin" ||
        t.role_name?.toLowerCase() === "apiadmin" ||
        t.role_name?.toLowerCase() === "api_admin"
    ) || false;

  const selectedTenantName =
    isSuperAdmin && !selectedTenantId
      ? "Select a Tenant"
      : tenants.find(
        (t: any) => String(t.tenant_id) === String(selectedTenantId)
      )?.tenant_name ||
      tenants[0]?.tenant_name ||
      "";
  const route = all_routes;
  const location = useLocation();
  const dispatch = useDispatch();

  const mobileSidebar = useSelector(
    (state: any) => state.commonSlice.mobileSidebar
  );
  const miniSidebar = useSelector(
    (state: any) => state.commonSlice.miniSidebar
  );
  const expandMenu = useSelector((state: any) => state.commonSlice.expandMenu);

  useEffect(() => {
    // Update body classes based on sidebar state
    if (miniSidebar) {
      document.body.classList.add("mini-sidebar");
    } else {
      document.body.classList.remove("mini-sidebar");
    }

    if (expandMenu) {
      document.body.classList.add("expand-menu");
    } else {
      document.body.classList.remove("expand-menu");
    }

    // Handle mobile sidebar
    if (mobileSidebar) {
      document.body.classList.add("slide-nav");
    } else {
      document.body.classList.remove("slide-nav");
    }
  }, [miniSidebar, expandMenu, mobileSidebar]);

  const toggleMobileSidebar = () => {
    dispatch(setMobileSidebar(!mobileSidebar));
  };
  const toggleMiniSidebar = () => {
    dispatch(setMiniSidebar(!miniSidebar));
  };
  const toggleExpandMenu = () => {
    dispatch(setExpandMenu(true));
  };
  const toggleExpandMenu2 = () => {
    dispatch(setExpandMenu(false));
  };

  const isLockScreen = location.pathname === "/lock-screen";

  if (isLockScreen) {
    return null;
  }

  const toggleSidebar = (title: any) => {
    localStorage.setItem("menuOpened", title);
    if (title === subOpen) {
      setSubopen("");
    } else {
      setSubopen(title);
    }
  };

  const toggleSubsidebar = (subitem: any) => {
    if (subitem === subsidebar) {
      setSubsidebar("");
    } else {
      setSubsidebar(subitem);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((err) => { });
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch((err) => { });
        }
        setIsFullscreen(false);
      }
    }
  };

  const handleLogout = () => {
    logout(); // Clear all authentication data from context and localStorage
    navigate(route.login); // Redirect to login page
  };


  return (
    <>
      {/* Header */}
      <div className="header">
        {/* Logo */}
        <div
          className="header-left active"
          onMouseEnter={toggleExpandMenu}
          onMouseLeave={toggleExpandMenu2}
        >
          <Link to={route.dealsDashboard} className="logo logo-normal">
            <ImageWithBasePath src="assets/img/Logo.png" alt="Logo" />
            <ImageWithBasePath
              src="assets/img/Logo.png"
              className="white-logo"
              alt="Logo"
            />
          </Link>
          <Link to={route.dealsDashboard} className="logo-small">
            <ImageWithBasePath src="assets/img/Logo.png" alt="Logo" />
          </Link>
          <Link id="toggle_btn" to="#" onClick={toggleMiniSidebar}>
            <i className="ti ti-arrow-bar-to-left" />
          </Link>
        </div>
        {/* /Logo */}
        <Link
          id="mobile_btn"
          className="mobile_btn"
          to="#sidebar"
          onClick={toggleMobileSidebar}
        >
          <span className="bar-icon">
            <span />
            <span />
            <span />
          </span>
        </Link>
        <div className="header-user">
          <ul className="nav user-menu">
            {/* Search */}
            <UniversalSearch />
            {/* /Search */}

            {!isSuperAdmin && (
              <li className="nav-item dropdown align-items-center justify-content-center d-flex me-3">
                <div ref={agentStatusDropdownRef}>
                  <button
                    className="btn btn-lg dropdown-toggle d-flex align-items-center gap-2"
                    type="button"
                    disabled={isAgentStatusLoading}
                    onClick={() => setIsAgentStatusDropdownOpen(!isAgentStatusDropdownOpen)}
                    style={{ borderRadius: "20px", padding: "4px 12px", border: "1px solid #e0e0e0", backgroundColor: "#fff", color: "#333", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}
                  >
                    {isAgentStatusLoading ? (
                      <div className="spinner-border text-secondary" role="status" style={{ width: "12px", height: "12px", borderWidth: "0.15em" }}></div>
                    ) : (
                      <span className={`status-indicator ${currentUserAgentStatus?.agent_status.agent_status_key === 'ONLINE' ? 'bg-success' : currentUserAgentStatus?.agent_status.agent_status_key === 'AWAY' ? 'bg-warning' : 'bg-danger'}`} style={{ width: "10px", height: "10px", borderRadius: "50%", display: "inline-block" }}></span>
                    )}
                    <span style={{ fontWeight: 500 }}>
                      {isAgentStatusLoading ? 'Loading...' : (currentUserAgentStatus ? currentUserAgentStatus.agent_status.agent_status_display_name : 'Offline')}
                    </span>
                  </button>
                  {isAgentStatusDropdownOpen && (
                    <div className="dropdown-menu show dropdown-menu-end" style={{ position: "absolute", top: "100%", right: "0", zIndex: 1000, marginTop: "0.25rem", minWidth: "220px", padding: "8px 0", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", border: "1px solid #e0e0e0" }}>
                      {agentStatuses.map((status: AgentStatus) => (
                        <button
                          key={status.agent_status_id}
                          className={`dropdown-item d-flex align-items-center justify-content-between py-2 px-3`}
                          onClick={() => handleAgentStatusChange(status.agent_status_id)}
                          style={{ cursor: "pointer", backgroundColor: currentUserAgentStatus?.agent_status.agent_status_id === status.agent_status_id ? '#f8f9fa' : 'transparent', borderBottom: "none" }}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <span className={`status-indicator ${status.agent_status_key === 'ONLINE' ? 'bg-success' : status.agent_status_key === 'AWAY' ? 'bg-warning' : 'bg-danger'}`} style={{ width: "12px", height: "12px", borderRadius: "50%", display: "inline-block" }}></span>
                            <div className="d-flex flex-column text-start">
                              <span style={{ fontWeight: 400, color: "#333", fontSize: "14px" }}>{status.agent_status_display_name}</span>
                              {status.agent_status_key === 'ONLINE' && <span style={{ fontSize: "12px", color: "#6c757d" }}>Available for chat</span>}
                              {status.agent_status_key === 'OFFLINE' && <span style={{ fontSize: "12px", color: "#6c757d" }}>Mute chat notifications</span>}
                              {status.agent_status_key === 'AWAY' && <span style={{ fontSize: "12px", color: "#6c757d" }}>Unavailable for chat</span>}
                            </div>
                          </div>
                          {currentUserAgentStatus?.agent_status.agent_status_id === status.agent_status_id && (
                            <i className="ti ti-check text-dark" style={{ fontSize: "16px" }} />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            )}

            {/* Tenant Dropdown */}
            <li className="nav-item dropdown tenant-select">
              <Link
                to="#"
                className="dropdown-toggle nav-link"
                data-bs-toggle="dropdown"
              >
                <span className="user-img">
                  <div className="tenant-info">
                    <i className="ti ti-building" />
                    <span className="tenant-name">{selectedTenantName}</span>
                  </div>
                  {/* <span className="status online" /> */}
                </span>
              </Link>
              <div
                className="dropdown-menu p-2"
                style={{ maxHeight: "200px", overflowY: "auto" }}
              >
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Search tenants..."
                  value={tenantSearch ?? ""}
                  onChange={(e) => setTenantSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                />
                {/* Show "All Tenants" option for Super Admin */}
                {isSuperAdmin && (
                  <button
                    className={`dropdown-item d-flex align-items-center text-start ${!selectedTenantId ? "active" : ""
                      }`}
                    style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                    onClick={() => {
                      setSelectedTenantId(null);
                      localStorage.removeItem("selectedTenantId");
                      localStorage.removeItem("currency_code");
                    }}
                  >
                    <i className="ti ti-world me-2" />
                    <span className="tenant-name">Select a Tenant</span>
                  </button>
                )}
                {filteredTenants.map((t: any) => (
                  <button
                    key={t.tenant_id}
                    className="dropdown-item d-flex align-items-center text-start"
                    style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                    onClick={() => {
                      const tenantIdStr = String(t.tenant_id);
                      setSelectedTenantId(tenantIdStr);
                      localStorage.setItem("selectedTenantId", tenantIdStr);
                      const tenantCurrency =
                        t.lifecycle_types?.find((lt: any) => lt.currency_code)?.currency_code ||
                        t.lifecycle_types?.[0]?.currency_code;
                      if (tenantCurrency) {
                        storeCurrencyCode(tenantCurrency);
                      } else {
                        localStorage.removeItem("currency_code");
                      }
                    }}
                  >
                    <i className="ti ti-building me-2" />
                    <span className="tenant-name">{t.tenant_name}</span>
                  </button>
                ))}

                {filteredTenants.length === 0 && (
                  <div className="text-muted text-center">No tenants found</div>
                )}
              </div>
            </li>
            {/* /Tenant Dropdown */}

            <li>
              <div
                className="sidebar sidebar-horizontal"
                id="horizontal-single"
              >
                <div className="sidebar-menu">
                  <div className="main-menu">
                    <ul className="nav-menu">
                      <li className="menu-title">
                        <span>Main</span>
                      </li>
                      {HorizontalSidebarData?.map((mainMenu, index) => (
                        <React.Fragment key={`main-${index}`}>
                          {mainMenu?.menu?.map((data, i) => (
                            <li className="submenu" key={`menu-${i}`}>
                              <Link
                                to="#"
                                className={`
														${data?.subMenus
                                    ?.map((link: any) => link?.route)
                                    .includes(location.pathname)
                                    ? "active"
                                    : ""
                                  } ${subOpen === data.menuValue ? "subdrop" : ""}`}
                                onClick={() => toggleSidebar(data.menuValue)}
                              >
                                <i className={`ti ti-${data.icon}`}></i>
                                <span>{data.menuValue}</span>
                                <span className="menu-arrow"></span>
                              </Link>

                              {/* First-level Submenus */}
                              <ul
                                style={{
                                  display:
                                    subOpen === data.menuValue
                                      ? "block"
                                      : "none",
                                }}
                              >
                                {data?.subMenus?.map((subMenu: any, j) => (
                                  <li
                                    key={`submenu-${j}`}
                                    className={
                                      subMenu?.customSubmenuTwo ? "submenu" : ""
                                    }
                                  >
                                    <Link
                                      to={subMenu?.route || "#"}
                                      className={`${subMenu?.subMenusTwo
                                        ?.map((link: any) => link?.route)
                                        .includes(location.pathname) ||
                                        subMenu?.route === location.pathname
                                        ? "active"
                                        : ""
                                        } ${subsidebar === subMenu.menuValue
                                          ? "subdrop"
                                          : ""
                                        }`}
                                      onClick={() =>
                                        toggleSubsidebar(subMenu.menuValue)
                                      }
                                    >
                                      <span>{subMenu?.menuValue}</span>
                                      {subMenu?.customSubmenuTwo && (
                                        <span className="menu-arrow"></span>
                                      )}
                                    </Link>

                                    {/* Check if `customSubmenuTwo` exists */}
                                    {subMenu?.customSubmenuTwo &&
                                      subMenu?.subMenusTwo && (
                                        <ul
                                          style={{
                                            display:
                                              subsidebar === subMenu.menuValue
                                                ? "block"
                                                : "none",
                                          }}
                                        >
                                          {subMenu.subMenusTwo.map(
                                            (subMenuTwo: any, k: number) => (
                                              <li key={`submenu-two-${k}`}>
                                                <Link
                                                  className={
                                                    subMenuTwo.route ===
                                                      location.pathname
                                                      ? "active"
                                                      : ""
                                                  }
                                                  to={subMenuTwo.route}
                                                >
                                                  {subMenuTwo.menuValue}
                                                </Link>
                                              </li>
                                            )
                                          )}
                                        </ul>
                                      )}
                                  </li>
                                ))}
                              </ul>
                            </li>
                          ))}
                        </React.Fragment>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </li>
            {/* Notifications */}

            <TaskNotification />

            {/* /Notifications */}
            {/* Nav List */}
            <li className="nav-item nav-list">
              <ul className="nav">
                <li>
                  <Link
                    to="#"
                    onClick={toggleFullscreen}
                    className="btn btn-icon border btn-menubar btnFullscreen"
                  >
                    <i className="ti ti-maximize"></i>
                  </Link>
                </li>
              </ul>
            </li>
            <li className="nav-item dropdown has-arrow main-drop">
              <Link
                to="#"
                className="nav-link userset"
                data-bs-toggle="dropdown"
              >
                <span className="user-info">
                  <span className="user-letter">
                    <img
                      src={avatarUrl}
                      className="img-fluid rounded-circle"
                      style={{ width: 32, height: 32, objectFit: "cover" }}
                      alt="Profile"
                    />
                  </span>
                  {!isSuperAdmin && (
                    <span className={`badge rounded-pill ${currentUserAgentStatus?.agent_status.agent_status_key === 'ONLINE' ? 'badge-success' : currentUserAgentStatus?.agent_status.agent_status_key === 'AWAY' ? 'badge-warning' : 'badge-danger'}`} />
                  )}
                </span>
              </Link>
              <div className={` dropdown-menu  menu-drop-user `}>
                <div className="profilename">
                  <Link className="dropdown-item text-start" to={route.dealsDashboard} style={{ textAlign: 'left' }}>
                    <i className="ti ti-layout-2" /> Dashboard
                  </Link>
                  <Link className="dropdown-item text-start" to={route.profile} style={{ textAlign: 'left' }}>
                    <i className="ti ti-user-pin" /> My Profile
                  </Link>
                  <button
                    className="dropdown-item text-start"
                    onClick={handleLogout}
                    style={{
                      border: "none",
                      background: "none",
                      width: "100%",
                      textAlign: "left",
                    }}
                  >
                    <i className="ti ti-lock" /> Logout
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
        <div className="dropdown mobile-user-menu">
          <Link
            to="#"
            className="nav-link dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fa fa-ellipsis-v" />
          </Link>
          <div className={` dropdown-menu `}>
            <button
              type="button"
              className="dropdown-item mobile-tenant-selector"
              onClick={(e) => {
                e.stopPropagation();
                const mobileMenuToggle = document.querySelector(
                  ".mobile-user-menu .dropdown-toggle"
                );
                if (mobileMenuToggle) {
                  (mobileMenuToggle as HTMLElement).click();
                }
                setMobileTenantDropdownOpen(true);
              }}
            >
              <div className="d-flex align-items-center">
                <i className="ti ti-building me-2" />
                <span className="tenant-name">{selectedTenantName}</span>
              </div>
              <i className="ti ti-chevron-right" />
            </button>
            <div className="dropdown-divider"></div>
            <Link className="dropdown-item" to={route.dealsDashboard}>
              <i className="ti ti-layout-2" /> Dashboard
            </Link>
            <Link className="dropdown-item" to={route.profile}>
              <i className="ti ti-user-pin" /> My Profile
            </Link>
            <button
              className="dropdown-item"
              onClick={handleLogout}
              style={{
                border: "none",
                background: "none",
                textAlign: "left",
                width: "100%",
              }}
            >
              <i className="ti ti-lock" /> Logout
            </button>
          </div>
        </div>
        <div
          className={`mobile-tenant-panel-backdrop ${mobileTenantDropdownOpen ? "show" : ""}`}
          onClick={() => {
            setMobileTenantDropdownOpen(false);
            setMobileTenantSearch("");
          }}
        />
        <div className={`mobile-tenant-panel ${mobileTenantDropdownOpen ? "show" : ""}`}>
          <div className="mobile-tenant-panel-header">
            <span>Select Tenant</span>
            <button
              type="button"
              className="mobile-tenant-panel-close"
              onClick={() => {
                setMobileTenantDropdownOpen(false);
                setMobileTenantSearch("");
              }}
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="tenant-search-wrap">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search tenants..."
              value={mobileTenantSearch ?? ""}
              onChange={(e) => setMobileTenantSearch(e.target.value)}
            />
          </div>
          <div className="tenant-list">
            {isSuperAdmin && (
              <button
                className={`tenant-option ${!selectedTenantId ? "active" : ""}`}
                onClick={() => {
                  setSelectedTenantId(null);
                  localStorage.removeItem("selectedTenantId");
                  localStorage.removeItem("currency_code");
                  setMobileTenantDropdownOpen(false);
                  setMobileTenantSearch("");
                }}
              >
                <i className="ti ti-world me-2" />
                <span className="tenant-name">Select a Tenant</span>
              </button>
            )}
            {filteredMobileTenants.map((t: any) => (
              <button
                key={t.tenant_id}
                className={`tenant-option ${String(t.tenant_id) === String(selectedTenantId) ? "active" : ""
                  }`}
                onClick={() => {
                  const tenantIdStr = String(t.tenant_id);
                  setSelectedTenantId(tenantIdStr);
                  localStorage.setItem("selectedTenantId", tenantIdStr);
                  const tenantCurrency =
                    t.lifecycle_types?.find((lt: any) => lt.currency_code)?.currency_code ||
                    t.lifecycle_types?.[0]?.currency_code;
                  if (tenantCurrency) {
                    storeCurrencyCode(tenantCurrency);
                  } else {
                    localStorage.removeItem("currency_code");
                  }
                  setMobileTenantDropdownOpen(false);
                  setMobileTenantSearch("");
                }}
              >
                <i className="ti ti-building me-2" />
                <span className="tenant-name">{t.tenant_name}</span>
              </button>
            ))}
            {filteredMobileTenants.length === 0 && (
              <div className="text-muted text-center p-2">No tenants found</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
